import clsx from 'clsx';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface DocumentNavigationProps {
  pageNumber: number;
  /** First navigable page — 1 for a whole document, the segment start for a page window. */
  firstPage: number;
  /** Last navigable page — `numPages` for a whole document, 0 while the PDF is loading. */
  lastPage: number;
  /** Discreet line under the counter giving window context, e.g. «Páginas 6–8 del archivo original». */
  windowCaption?: string;
  onPrev: () => void;
  onNext: () => void;
}

/**
 * Page navigation strip pinned to the bottom of the PDF column. Uses a frosted-glass
 * background so when a page is taller than its container, the canvas behind the strip
 * still hints at what is being scrolled past. Sticky positioning keeps the controls in
 * view without nudging the canvas flow.
 *
 * Navigation is bounded to `[firstPage, lastPage]`; page numbers stay absolute to the
 * loaded PDF, so a segment of a split upload reads «Página 6 / 8» rather than renumbering.
 *
 * When the window has ≤ 7 pages we add a dot pager (iOS-style page control); past
 * that the dots become too small to be useful and only the counter shows.
 */
export function DocumentNavigation({
  pageNumber,
  firstPage,
  lastPage,
  windowCaption,
  onPrev,
  onNext,
}: DocumentNavigationProps): JSX.Element {
  const atStart = pageNumber <= firstPage;
  const atEnd = pageNumber >= lastPage || lastPage === 0;
  const windowSize = lastPage - firstPage + 1;
  const showDots = lastPage > 0 && windowSize <= 7;

  return (
    <div className="sticky bottom-0 z-10 flex items-center justify-between gap-3 border-t border-slate-200/70 bg-white/85 px-4 py-2.5 backdrop-blur supports-[backdrop-filter]:bg-white/65">
      <IconButton onClick={onPrev} disabled={atStart} ariaLabel="Página anterior">
        <ChevronLeft className="h-4 w-4" aria-hidden strokeWidth={2.25} />
      </IconButton>
      <div className="flex flex-1 flex-col items-center justify-center">
        <div className="flex items-center gap-3 text-sm">
          <span className="tabular-nums text-slate-600">
            Página <span className="font-semibold text-slate-900">{pageNumber}</span>
            <span className="mx-1 text-slate-300">/</span>
            <span className="font-semibold text-slate-900">{lastPage || '—'}</span>
          </span>
          {showDots ? (
            <DotPager firstPage={firstPage} lastPage={lastPage} current={pageNumber} />
          ) : null}
        </div>
        {windowCaption !== undefined ? (
          <span className="text-xs text-slate-400">{windowCaption}</span>
        ) : null}
      </div>
      <IconButton onClick={onNext} disabled={atEnd} ariaLabel="Página siguiente">
        <ChevronRight className="h-4 w-4" aria-hidden strokeWidth={2.25} />
      </IconButton>
    </div>
  );
}

interface IconButtonProps {
  onClick: () => void;
  disabled: boolean;
  ariaLabel: string;
  children: React.ReactNode;
}

function IconButton({ onClick, disabled, ariaLabel, children }: IconButtonProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={clsx(
        'inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200/70 bg-white text-slate-700 shadow-sm transition-all duration-150',
        'hover:-translate-y-0.5 hover:border-slate-300 hover:shadow',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0 disabled:hover:shadow-sm',
      )}
    >
      {children}
    </button>
  );
}

function DotPager({
  firstPage,
  lastPage,
  current,
}: {
  firstPage: number;
  lastPage: number;
  current: number;
}): JSX.Element {
  return (
    <ol className="flex items-center gap-1" aria-hidden>
      {Array.from({ length: lastPage - firstPage + 1 }, (_, i) => firstPage + i).map((page) => (
        <li
          key={page}
          className={clsx(
            'h-1.5 rounded-full transition-all duration-200',
            page === current ? 'w-4 bg-slate-900' : 'w-1.5 bg-slate-300',
          )}
        />
      ))}
    </ol>
  );
}
